import * as dateFns from 'date-fns';

import { DayMessageMap, getChatKey } from './getChatKey';
import { DayShiftMap, EmployeeMap, ShiftMap } from '../api/model';
import {
  MsgVisibilityOldEnum,
  TeamShiftStatusEnum,
} from '@pdcplan/shared/enums';
import { dateMath, dayKey, searchString } from '@pdcfrontendui/utils';

import { Period } from './dates';
import { TeamShift } from '@pdcplan/shared/api/TeamPlan';
import { ListViewTab } from '../routes';
import { currentLanguage } from '../currentLanguage';
import { shouldShowAsUnread } from '../Chat/chatUtils';

export function getSearchFilteredShifts(
  dayMap: Record<string, TeamShift[]>,
  employees: EmployeeMap,
  search: string
) {
  return Object.entries(dayMap).reduce<Record<string, TeamShift[]>>(
    (acc, [key, shifts]) => {
      acc[key] = shifts.filter((shift) => {
        const employee = employees[shift.personId];
        return !!employee && searchString(search, employee.name);
      });
      return acc;
    },
    {}
  );
}

function shiftSort(a: TeamShift, b: TeamShift) {
  if (dateFns.isEqual(a.period.from, b.period.from)) {
    if (dateFns.isAfter(a.period.to, b.period.to)) {
      // if the dates are the same, sort the items by the end time
      return 1;
    } else {
      return -1;
    }
  } else if (dateFns.isAfter(a.period.from, b.period.from)) {
    // if date a is after b, make a come after b
    return 1;
  } else {
    // else, a must be before b, make a come before b
    return -1;
  }
}

function dayKeysToMap(keys: string[]) {
  return keys.reduce<Record<string, TeamShift[]>>((acc, key) => {
    acc[key] = [];
    return acc;
  }, {});
}

/**
 *
 * Get an object where keys are the days of the period, and values are arrays of shifts to be rendered in the list view
 * Also returns a lookup of which shifts have messages and which have unread messages, for fast lookup while rendering
 */
export function getDayShiftsMap(
  period: Period, // TODO: Ensure that the period is always from start of day to end of day
  shifts: ShiftMap,
  dayMessageMap: DayMessageMap,
  userId: number
) {
  // Subtract one millisecond from the end of the period to ensure that we don't include an extra day
  const dayKeys = dateFns
    .eachDayOfInterval({
      start: period.from,
      end: dateFns.subMilliseconds(period.to, 1),
    })
    .map(dayKey);
  const dayShiftsMap: DayShiftMap = {
    planned: dayKeysToMap(dayKeys),
    pending: dayKeysToMap(dayKeys),
    changes: dayKeysToMap(dayKeys),
    hasMessageLookup: {},
    hasUnreadLookup: {},
  };
  const shiftArr = Object.values(shifts).sort(shiftSort);
  for (const shift of shiftArr) {
    const currentDate = dateMath.floorDay(shift.period.from);
    // Allow shift to be pushed into more than one day
    while (currentDate < shift.period.to && currentDate < period.to) {
      const currentKey = dayKey(currentDate);
      switch (shift.status) {
        // Special case for substitute, they are both planned and changes
        // Means that there has been a swap, so we want to see it in both tabs
        case TeamShiftStatusEnum.substitute:
          dayShiftsMap.planned[currentKey]?.push(shift);
          dayShiftsMap.changes[currentKey]?.push(shift);
          break;
        // Exchange items are both planned and pending changes
        case TeamShiftStatusEnum.swapActionRequired:
          dayShiftsMap.planned[currentKey]?.push(shift);
          dayShiftsMap.pending[currentKey]?.push(shift);
          break;
        case TeamShiftStatusEnum.planned:
        case TeamShiftStatusEnum.noActivities:
          dayShiftsMap.planned[currentKey]?.push(shift);
          break;
        // Everything that requires immediate action
        case TeamShiftStatusEnum.offered:
        case TeamShiftStatusEnum.actionRequired:
        case TeamShiftStatusEnum.deputyOffered:
        case TeamShiftStatusEnum.offerActionRequired:
          dayShiftsMap.pending[currentKey]?.push(shift);
          break;
        case TeamShiftStatusEnum.replaced:
        case TeamShiftStatusEnum.absent: // absent means that no one is assigned to the shift, and that we don't want to find a replacement
          dayShiftsMap.changes[currentKey]?.push(shift);
          break;
      }
      const chatKeyPrivate = getChatKey(
        shift.personId,
        currentDate,
        MsgVisibilityOldEnum.planner
      );
      const chatKeyPublic = getChatKey(
        shift.personId,
        currentDate,
        MsgVisibilityOldEnum.employee
      );
      const internalMessages = dayMessageMap[chatKeyPrivate] ?? null;
      const messages = dayMessageMap[chatKeyPublic] ?? null;
      const hasInternalMessage = !!internalMessages?.length;
      if (hasInternalMessage) {
        dayShiftsMap.hasMessageLookup[shift.id] = true;
      }
      const hasMessage = !!messages?.length;
      if (hasMessage) {
        dayShiftsMap.hasMessageLookup[shift.id] = true;
      }
      const hasUnreadMessage = !!messages?.some((message) =>
        shouldShowAsUnread(message, userId)
      );
      const hasUnreadInternalMessage = !!internalMessages?.some((message) =>
        shouldShowAsUnread(message, userId)
      );
      if (hasUnreadMessage || hasUnreadInternalMessage) {
        dayShiftsMap.hasUnreadLookup[shift.id] = true;
      }
      currentDate.setTime(currentDate.getTime() + 24 * 60 * 60 * 1000);
    }
  }
  return dayShiftsMap;
}

export function getShiftStatusText(
  status: TeamShiftStatusEnum,
  selectedTab?: ListViewTab
) {
  switch (status) {
    case TeamShiftStatusEnum.planned:
      return currentLanguage.ShiftStatusPlanned;
    case TeamShiftStatusEnum.actionRequired:
      return currentLanguage.ShiftStatusActionRequired;
    case TeamShiftStatusEnum.offered:
      return currentLanguage.ShiftStatusOffered;
    case TeamShiftStatusEnum.replaced:
      return currentLanguage.ShiftStatusReplaced;
    case TeamShiftStatusEnum.absent:
      return currentLanguage.ShiftStatusAbsent;
    case TeamShiftStatusEnum.substitute:
      return currentLanguage.ShiftStatusSubstitute;
    case TeamShiftStatusEnum.noActivities:
      return currentLanguage.ShiftStatusNoActivities;
    case TeamShiftStatusEnum.offerActionRequired:
      if (!selectedTab || selectedTab === ListViewTab.Planned) {
        return currentLanguage.AwaitingApproveHandover;
      } else {
        return currentLanguage.ApproveRejectHandover;
      }
    case TeamShiftStatusEnum.swapActionRequired:
      if (!selectedTab || selectedTab === ListViewTab.Planned) {
        return currentLanguage.AwaitingApproveSwap;
      } else {
        return currentLanguage.ApproveRejectSwap;
      }
    case TeamShiftStatusEnum.deputyOffered:
      return currentLanguage.OfferedThroughTempAgency;
    default:
      return '';
  }
}

export function getListItemLabel(
  status: TeamShiftStatusEnum,
  shiftInformationLabel: string,
  selectedTab?: ListViewTab
): string {
  switch (status) {
    case TeamShiftStatusEnum.offerActionRequired:
      if (!selectedTab || selectedTab === ListViewTab.Planned) {
        return currentLanguage.AwaitingApproveHandover;
      } else {
        return currentLanguage.ApproveRejectHandover;
      }
    case TeamShiftStatusEnum.swapActionRequired:
      if (!selectedTab || selectedTab === ListViewTab.Planned) {
        return currentLanguage.AwaitingApproveSwap;
      } else {
        return currentLanguage.ApproveRejectSwap;
      }
    case TeamShiftStatusEnum.actionRequired:
      return currentLanguage.ShiftStatusActionRequired;
    case TeamShiftStatusEnum.offered:
      return currentLanguage.ShiftStatusOffered;
    case TeamShiftStatusEnum.replaced:
      return currentLanguage.ShiftStatusReplaced;
    case TeamShiftStatusEnum.absent:
      return currentLanguage.ShiftStatusAbsent;

    case TeamShiftStatusEnum.planned:
    case TeamShiftStatusEnum.noActivities:
    case TeamShiftStatusEnum.substitute:
      return shiftInformationLabel;
    case TeamShiftStatusEnum.deputyOffered:
      return currentLanguage.OfferedThroughTempAgency;

    default:
      return '';
  }
}

export const getShiftStatusStyling = (status: TeamShiftStatusEnum) => {
  switch (status) {
    case TeamShiftStatusEnum.actionRequired:
    case TeamShiftStatusEnum.offered:
    case TeamShiftStatusEnum.offerActionRequired:
    case TeamShiftStatusEnum.swapActionRequired:
    case TeamShiftStatusEnum.deputyOffered:
      return 'alert';
    case TeamShiftStatusEnum.substitute:
      return 'secondary';
    case TeamShiftStatusEnum.absent:
    case TeamShiftStatusEnum.replaced:
      return 'grey';
    case TeamShiftStatusEnum.planned:
    case TeamShiftStatusEnum.noActivities:
    default:
      return 'primary';
  }
};

/**
 * In some cases, we disable editing a shift even though it is technically possible.
 * Editing an offered shift is allowed by the backend, but it would cause the offer to be removed.
 * There are other ways to achieve the same thing, so we don't allow it in the frontend.
 */
export function shiftIsEditable(shift: TeamShift) {
  if (!shift.isTimeEditable) {
    return false;
  }
  switch (shift.status) {
    case TeamShiftStatusEnum.actionRequired:
    case TeamShiftStatusEnum.absent:
    case TeamShiftStatusEnum.offered:
    case TeamShiftStatusEnum.swapActionRequired:
    case TeamShiftStatusEnum.offerActionRequired:
    case TeamShiftStatusEnum.deputyOffered:
      return false;
    default:
      return true;
  }
}

/**
 * Delete is always allowed by the backend, but we disable it in the frontend in some cases.
 * These include cases where there are other ways to achieve the same thing.
 */
export function shiftIsDeleteable(shift: TeamShift) {
  if (shiftIsEditable(shift)) {
    return true;
  }
  switch (shift.status) {
    case TeamShiftStatusEnum.planned:
    case TeamShiftStatusEnum.replaced:
    case TeamShiftStatusEnum.substitute:
      return true;
    default:
      return false;
  }
}

export function shiftRequiresAction(shift: TeamShift): shift is TeamShift & {
  status:
    | TeamShiftStatusEnum.actionRequired
    | TeamShiftStatusEnum.offerActionRequired
    | TeamShiftStatusEnum.swapActionRequired
    // Doesn't really require action, but the offer can be cancelled
    | TeamShiftStatusEnum.offered;
} {
  switch (shift.status) {
    case TeamShiftStatusEnum.actionRequired:
    case TeamShiftStatusEnum.offerActionRequired:
    case TeamShiftStatusEnum.swapActionRequired:
    case TeamShiftStatusEnum.offered:
      return true;
    default:
      return false;
  }
}

export function shouldShowRegisterAbsence(
  shift: TeamShift
): shift is TeamShift & {
  status:
    | TeamShiftStatusEnum.planned
    | TeamShiftStatusEnum.substitute
    | TeamShiftStatusEnum.noActivities;
} {
  switch (shift.status) {
    case TeamShiftStatusEnum.planned:
    case TeamShiftStatusEnum.substitute:
    case TeamShiftStatusEnum.noActivities:
      return true;
    default:
      return false;
  }
}
